import { z } from 'zod';
import { ReportPadError } from './errors.js';
import { newId } from './ids.js';
import { describeOperations } from './activity-text.js';
import { KpiWidgetSchema, ChartWidgetSchema, TableWidgetSchema, TextWidgetSchema, LayoutItemSchema, PrintOptionsSchema, GRID_COLUMNS, defaultLayoutSize, nextFreeRow, assertNoNewOverlaps } from './report.js';
import { LIMITS } from './limits.js';
import { validateDefinition } from './validation.js';
/**
 * Structured report mutations.
 *
 * Both the browser editor and MCP `update_report` speak this vocabulary, so
 * there is exactly one code path that can change a definition, and exactly one
 * place where the *resulting* definition is validated before it is committed.
 *
 * Every operation is a pure function of (definition, op) -> definition. No
 * free-form patch, no arbitrary JSON merge: an agent cannot reach a shape that
 * the editor could not also have produced.
 *
 * **Determinism.** The browser applies operations locally for instant feedback
 * and then sends the *same* operation objects to the server. Applying one twice
 * must therefore produce the same definition both times. `add_widget` and
 * `duplicate_widget` mint an id when none is supplied, which would break that —
 * client and server would each invent a different id and the widget would
 * change identity under the user's cursor, taking the selection and the undo
 * stack with it. So an interactive client always supplies the id; minting is a
 * convenience for agents making one-shot calls, where there is no second
 * application to disagree with.
 */
const widgetIdRef = z.string().min(1).max(64);
/**
 * Strips a `.default()` wrapper so a patch field can be genuinely absent.
 *
 * `someSchema.default(x).optional()` is a trap: zod still materialises `x` when
 * the key is missing, so a patch naming one field arrives carrying several the
 * caller never sent. On `update_widget` that fails loudly, because `applyPatch`
 * rejects fields foreign to the widget's type. On `set_print_options` it would
 * fail *silently and destructively* — a caller toggling one flag would reset
 * `print.order` to `[]` and discard the author's print sequence.
 *
 * Unwrapping only removes the default for an absent key. A patch that does
 * supply the object still gets that object's own inner defaults filled in,
 * which is what a whole-value replacement should do.
 */
function withoutDefault(schema) {
    const def = schema.def;
    return def.type === 'default'
        ? schema.unwrap()
        : schema;
}
/** A widget submitted for creation: same shape as a stored widget, but the id
 *  is optional because the server mints stable ids. */
const NewWidgetSchema = z.discriminatedUnion('type', [
    KpiWidgetSchema.omit({ id: true }).extend({ id: widgetIdRef.optional() }),
    ChartWidgetSchema.omit({ id: true }).extend({ id: widgetIdRef.optional() }),
    TableWidgetSchema.omit({ id: true }).extend({ id: widgetIdRef.optional() }),
    TextWidgetSchema.omit({ id: true }).extend({ id: widgetIdRef.optional() })
]);
/**
 * Partial widget update. `type` may not change — changing a KPI into a table
 * would silently discard its configuration, so callers remove and re-add.
 *
 * `.strict()`: a plain `z.object()` silently drops a key it doesn't
 * recognise, which turned a typo'd field (e.g. `grupBy` for `groupBy`) into a
 * no-op patch — same `definition` in, same `definition` out, still a 200. A
 * retrying agent hitting that typo repeatedly still bumps `revision` and
 * appends a new (identical) row to revision history on every call, and
 * `apps/server/src/repositories/reports.ts` prunes history to
 * `LIMITS.maxRetainedRevisions` on every append — so enough no-op retries
 * silently evict real revisions from the only copy of this report's history.
 * Rejecting the unrecognised key outright turns that into a loud, immediate
 * validation error instead.
 */
const WidgetPatchSchema = z.object({
    title: z.string().min(1).max(LIMITS.maxLabelChars).optional(),
    caption: z.string().max(200).nullable().optional(),
    metric: withoutDefault(KpiWidgetSchema.shape.metric).optional(),
    format: withoutDefault(KpiWidgetSchema.shape.format).optional(),
    chartType: withoutDefault(ChartWidgetSchema.shape.chartType).optional(),
    dimensionColumnId: withoutDefault(ChartWidgetSchema.shape.dimensionColumnId).optional(),
    series: withoutDefault(ChartWidgetSchema.shape.series).optional(),
    stacked: withoutDefault(ChartWidgetSchema.shape.stacked).optional(),
    maxCategories: withoutDefault(ChartWidgetSchema.shape.maxCategories).optional(),
    columns: withoutDefault(TableWidgetSchema.shape.columns).optional(),
    sort: withoutDefault(TableWidgetSchema.shape.sort).nullable().optional(),
    pageSize: withoutDefault(TableWidgetSchema.shape.pageSize).optional(),
    groupBy: withoutDefault(TableWidgetSchema.shape.groupBy).nullable().optional(),
    defaultExpandedDepth: withoutDefault(TableWidgetSchema.shape.defaultExpandedDepth).optional(),
    body: withoutDefault(TextWidgetSchema.shape.body).optional()
}).strict();
/**
 * Partial print options.
 *
 * Built field by field rather than with `PrintOptionsSchema.partial()`, because
 * `.partial()` only makes keys optional — it leaves each field's `.default()`
 * intact, so an absent key would still arrive carrying its default value.
 */
const PrintOptionsPatchSchema = z.object({
    pageSize: withoutDefault(PrintOptionsSchema.shape.pageSize).optional(),
    orientation: withoutDefault(PrintOptionsSchema.shape.orientation).optional(),
    order: withoutDefault(PrintOptionsSchema.shape.order).optional(),
    hidden: withoutDefault(PrintOptionsSchema.shape.hidden).optional(),
    includeCoverSummary: withoutDefault(PrintOptionsSchema.shape.includeCoverSummary).optional(),
    includeSourceNotes: withoutDefault(PrintOptionsSchema.shape.includeSourceNotes).optional()
});
/** Where `place_row` puts its row: a page edge, or next to another widget. */
const AtSchema = z.union([
    z.literal('top'),
    z.literal('bottom'),
    z.object({ below: widgetIdRef }).strict(),
    z.object({ above: widgetIdRef }).strict()
]);
export const ReportOperationSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('rename_report'), title: z.string().min(1).max(LIMITS.maxLabelChars) }),
    z
        .object({
        op: z.literal('set_report_details'),
        subtitle: z.string().max(LIMITS.maxSubtitleChars).nullable().optional(),
        preparedFor: z.string().max(LIMITS.maxPreparedForChars).nullable().optional()
    })
        .strict()
        .refine((o) => o.subtitle !== undefined || o.preparedFor !== undefined, 'Nothing to change'),
    z.object({
        op: z.literal('add_widget'),
        widget: NewWidgetSchema,
        /** Optional explicit placement; otherwise the widget is appended below. */
        layout: LayoutItemSchema.omit({ widgetId: true }).partial().optional()
    }),
    z.object({ op: z.literal('update_widget'), widgetId: widgetIdRef, patch: WidgetPatchSchema }),
    z.object({ op: z.literal('remove_widget'), widgetId: widgetIdRef }),
    z.object({
        op: z.literal('duplicate_widget'),
        widgetId: widgetIdRef,
        /** Id for the copy. Supply it — see the determinism note below. */
        newWidgetId: widgetIdRef.optional()
    }),
    z.object({ op: z.literal('set_layout'), items: z.array(LayoutItemSchema).max(LIMITS.maxWidgetsPerReport) }),
    z.object({ op: z.literal('set_print_options'), print: PrintOptionsPatchSchema }),
    // Types only: every other check is in the `place_row` case, so each refusal
    // names the ids or values involved.
    z
        .object({
        op: z.literal('place_row'),
        widgetIds: z.array(widgetIdRef),
        at: AtSchema,
        widths: z.array(z.number()).optional()
    })
        .strict()
]);
// Kept separate from LEGACY_SUMMARY_OPERATION_NAMES in activity-text.ts, which must stay
// append-only even as this list changes, to keep recognising op names in old stored summaries.
export const OPERATION_NAMES = [
    'rename_report',
    'set_report_details',
    'add_widget',
    'update_widget',
    'remove_widget',
    'duplicate_widget',
    'set_layout',
    'set_print_options',
    'place_row'
];
// ---------------------------------------------------------------------------
function clone(value) {
    return structuredClone(value);
}
function requireWidget(definition, widgetId) {
    const widget = definition.widgets.find((w) => w.id === widgetId);
    if (!widget) {
        throw new ReportPadError('UNKNOWN_WIDGET_REFERENCE', `This report has no widget "${widgetId}".`, {
            issues: [
                {
                    code: 'UNKNOWN_WIDGET_REFERENCE',
                    message: `This report has no widget "${widgetId}". Call get_report to see the current widget ids.`,
                    location: { widgetId }
                }
            ]
        });
    }
    return widget;
}
/** Drops patch keys that do not belong to the widget's type, so a caller
 *  cannot smuggle a chart field onto a KPI and have it silently persisted. */
function applyPatch(widget, patch) {
    const next = clone(widget);
    const allowed = {
        kpi: ['title', 'caption', 'metric', 'format'],
        chart: ['title', 'chartType', 'dimensionColumnId', 'series', 'format', 'stacked', 'maxCategories'],
        table: ['title', 'columns', 'sort', 'pageSize', 'groupBy', 'defaultExpandedDepth'],
        text: ['title', 'body']
    };
    const keys = allowed[widget.type];
    const rejected = [];
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined)
            continue;
        if (!keys.includes(key)) {
            rejected.push(key);
            continue;
        }
        if (value === null) {
            delete next[key];
            continue;
        }
        next[key] = value;
    }
    if (rejected.length > 0) {
        throw new ReportPadError('REPORT_DEFINITION_INVALID', `Fields ${rejected.map((r) => `"${r}"`).join(', ')} do not apply to a ${widget.type} widget. Remove the widget and add a new one to change its type.`, { issues: rejected.map((field) => ({ code: 'REPORT_DEFINITION_INVALID', message: `"${field}" is not a ${widget.type} field.`, location: { widgetId: widget.id, path: field } })) });
    }
    return next;
}
function placeWidget(definition, widgetId, hint) {
    const widget = definition.widgets.find((w) => w.id === widgetId);
    const size = defaultLayoutSize(widget.type);
    return {
        widgetId,
        x: hint?.x ?? 0,
        y: hint?.y ?? nextFreeRow(definition.layout),
        w: hint?.w ?? size.w,
        h: hint?.h ?? size.h
    };
}
/** The lowest row a widget may start on: mirrors `y: ….max(1000)` in
 *  `LayoutItemSchema` (report.ts). A place_row test fails if they drift. */
const MAX_LAYOUT_Y = 1000;
function layoutInvalid(message, widgetId) {
    return new ReportPadError('LAYOUT_INVALID', message, {
        issues: [{ code: 'LAYOUT_INVALID', message, ...(widgetId ? { location: { widgetId } } : {}) }]
    });
}
/**
 * Not exported: geometry only, called after the `place_row` case has
 * validated its input. Lays the listed widgets out as one row at a cut line
 * that no remaining widget straddles, and moves everything at or below the
 * cut down by the row's height.
 */
function placeRow(items, rowItems, at, widths) {
    const listed = new Set(rowItems.map((r) => r.widgetId));
    const rest = items.filter((i) => !listed.has(i.widgetId));
    const straddles = (y) => rest.find((w) => w.y < y && y < w.y + w.h);
    let cut;
    if (at === 'top')
        cut = 0;
    else if (at === 'bottom')
        cut = rest.reduce((m, i) => Math.max(m, i.y + i.h), 0);
    else if ('below' in at) {
        const anchor = rest.find((i) => i.widgetId === at.below);
        cut = anchor.y + anchor.h;
        for (let w = straddles(cut); w; w = straddles(cut))
            cut = w.y + w.h;
    }
    else {
        const anchor = rest.find((i) => i.widgetId === at.above);
        cut = anchor.y;
        for (let w = straddles(cut); w; w = straddles(cut))
            cut = w.y;
    }
    const rowHeight = Math.max(...rowItems.map((r) => r.h));
    let x = 0;
    const row = rowItems.map((r, i) => {
        const item = { widgetId: r.widgetId, x, y: cut, w: widths[i], h: r.h };
        x += widths[i];
        return item;
    });
    return [...rest.map((i) => (i.y >= cut ? { ...i, y: i.y + rowHeight } : i)), ...row];
}
/** Even split of the grid: 12 / n, leftover columns to the leftmost widgets. */
function evenWidths(n) {
    const base = Math.floor(GRID_COLUMNS / n);
    const extra = GRID_COLUMNS % n;
    return Array.from({ length: n }, (_, i) => base + (i < extra ? 1 : 0));
}
/** Applies one operation. Does not validate the result — `applyOperations`
 *  does that once, after the whole batch. */
function applyOperation(definition, operation) {
    const next = clone(definition);
    switch (operation.op) {
        case 'rename_report':
            next.title = operation.title;
            return next;
        case 'set_report_details': {
            // null, or a string that is empty after trimming, deletes the field;
            // any other string sets it trimmed. rename_report keeps handling the
            // title, so these two fields stay in their own op.
            if (operation.subtitle !== undefined) {
                const trimmed = operation.subtitle === null ? '' : operation.subtitle.trim();
                if (trimmed)
                    next.subtitle = trimmed;
                else
                    delete next.subtitle;
            }
            if (operation.preparedFor !== undefined) {
                const trimmed = operation.preparedFor === null ? '' : operation.preparedFor.trim();
                if (trimmed)
                    next.preparedFor = trimmed;
                else
                    delete next.preparedFor;
            }
            return next;
        }
        case 'add_widget': {
            if (next.widgets.length >= LIMITS.maxWidgetsPerReport) {
                throw new ReportPadError('LIMIT_EXCEEDED', `A report may contain at most ${LIMITS.maxWidgetsPerReport} widgets.`, { details: { max: LIMITS.maxWidgetsPerReport } });
            }
            const requestedId = operation.widget.id;
            if (requestedId && next.widgets.some((w) => w.id === requestedId)) {
                throw new ReportPadError('DUPLICATE_WIDGET_ID', `Widget id "${requestedId}" is already used in this report.`);
            }
            const widget = { ...operation.widget, id: requestedId ?? newId('w') };
            next.widgets.push(widget);
            next.layout.items.push(placeWidget(next, widget.id, operation.layout));
            return next;
        }
        case 'update_widget': {
            const existing = requireWidget(next, operation.widgetId);
            const index = next.widgets.indexOf(existing);
            next.widgets[index] = applyPatch(existing, operation.patch);
            return next;
        }
        case 'remove_widget': {
            requireWidget(next, operation.widgetId);
            next.widgets = next.widgets.filter((w) => w.id !== operation.widgetId);
            next.layout.items = next.layout.items.filter((i) => i.widgetId !== operation.widgetId);
            next.print.order = next.print.order.filter((id) => id !== operation.widgetId);
            next.print.hidden = next.print.hidden.filter((id) => id !== operation.widgetId);
            return next;
        }
        case 'duplicate_widget': {
            const source = requireWidget(next, operation.widgetId);
            if (next.widgets.length >= LIMITS.maxWidgetsPerReport) {
                throw new ReportPadError('LIMIT_EXCEEDED', `A report may contain at most ${LIMITS.maxWidgetsPerReport} widgets.`);
            }
            if (operation.newWidgetId && next.widgets.some((w) => w.id === operation.newWidgetId)) {
                throw new ReportPadError('DUPLICATE_WIDGET_ID', `Widget id "${operation.newWidgetId}" is already used in this report.`);
            }
            const copy = {
                ...clone(source),
                id: operation.newWidgetId ?? newId('w'),
                title: `${source.title} (copy)`
            };
            next.widgets.push(copy);
            const original = next.layout.items.find((i) => i.widgetId === source.id);
            next.layout.items.push({
                widgetId: copy.id,
                x: original?.x ?? 0,
                y: nextFreeRow(next.layout),
                w: original?.w ?? defaultLayoutSize(copy.type).w,
                h: original?.h ?? defaultLayoutSize(copy.type).h
            });
            return next;
        }
        case 'set_layout': {
            // A GridStack drag sends every placement; an agent may send only some.
            // Widgets omitted from the payload keep their previous placement, so a
            // partial save cannot orphan a widget.
            const incoming = new Map(operation.items.map((item) => [item.widgetId, item]));
            const merged = [];
            for (const widget of next.widgets) {
                const item = incoming.get(widget.id) ?? next.layout.items.find((i) => i.widgetId === widget.id);
                merged.push(item ? { ...item, widgetId: widget.id } : placeWidget(next, widget.id));
            }
            next.layout.items = merged;
            return next;
        }
        case 'set_print_options': {
            next.print = { ...next.print, ...stripUndefined(operation.print) };
            return next;
        }
        case 'place_row': {
            const { widgetIds, at } = operation;
            const target = typeof at === 'string' ? null : 'below' in at ? at.below : at.above;
            for (const id of widgetIds)
                requireWidget(next, id);
            if (target !== null) {
                requireWidget(next, target);
                if (!next.layout.items.some((i) => i.widgetId === target)) {
                    throw layoutInvalid(`Widget ${target} has no layout entry.`, target);
                }
            }
            const maxRowWidgets = GRID_COLUMNS;
            if (widgetIds.length === 0 || widgetIds.length > maxRowWidgets) {
                throw layoutInvalid(`place_row takes 1 to ${maxRowWidgets} widgets; it was given ${widgetIds.length}.`);
            }
            const seen = new Set();
            for (const id of widgetIds) {
                if (seen.has(id))
                    throw layoutInvalid(`Widget ${id} is listed twice in place_row.`, id);
                seen.add(id);
            }
            if (target !== null && seen.has(target)) {
                throw layoutInvalid(`Widget ${target} cannot be placed next to itself: it is both in the row and its anchor.`, target);
            }
            const widths = operation.widths ?? evenWidths(widgetIds.length);
            if (widths.length !== widgetIds.length) {
                throw layoutInvalid(`place_row was given ${widths.length} widths for ${widgetIds.length} widgets.`);
            }
            for (const width of widths) {
                if (!Number.isInteger(width) || width < 1) {
                    throw layoutInvalid(`Width ${width} is not a whole number of columns of at least 1.`);
                }
            }
            const total = widths.reduce((sum, width) => sum + width, 0);
            if (total > GRID_COLUMNS) {
                throw layoutInvalid(`The widths add up to ${total} columns; the grid has ${GRID_COLUMNS}.`);
            }
            const rowItems = widgetIds.map((id) => {
                const item = next.layout.items.find((i) => i.widgetId === id);
                return { widgetId: id, h: item?.h ?? defaultLayoutSize(requireWidget(next, id).type).h };
            });
            const placed = placeRow(next.layout.items, rowItems, at, widths);
            const tooLow = placed.find((i) => i.y > MAX_LAYOUT_Y);
            if (tooLow) {
                throw layoutInvalid(`Widget ${tooLow.widgetId} would move to y ${tooLow.y}, below the last row (y ${MAX_LAYOUT_Y}).`, tooLow.widgetId);
            }
            next.layout.items = placed;
            return next;
        }
    }
}
function stripUndefined(value) {
    return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined));
}
/**
 * Applies a batch of operations and validates the complete result.
 *
 * Validation happens once, at the end, against the dataset schema. A batch
 * either lands whole or not at all — there is no half-applied definition.
 */
export function applyOperations(definition, operations, dataset) {
    if (operations.length === 0) {
        throw new ReportPadError('INVALID_REQUEST', 'At least one operation is required.');
    }
    let current = definition;
    for (const operation of operations) {
        current = applyOperation(current, operation);
    }
    assertNoNewOverlaps(definition, current);
    const validated = validateDefinition(current, dataset);
    return { definition: validated, summary: describeOperations(definition, operations) };
}
//# sourceMappingURL=operations.js.map